<script setup lang="ts">
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { ElButton, ElTooltip } from "element-plus";
import { RefreshLeft, RefreshRight } from "@element-plus/icons-vue";
import { useApiErrorMessage } from "@/shared/lib";
import { useUndoRedoStore } from "../model";

defineOptions({
  name: "MbUndoRedoButtons",
});

const { t } = useI18n();
const undoRedoStore = useUndoRedoStore();
// Тексты отказов ищутся сперва в зоне отмены (`undoRedo.errors.<признак>`),
// затем в общих (api-errors-R04, i18n-R06).
const { showApiError } = useApiErrorMessage(["undoRedo"]);

/**
 * Каждый отказ отмены и возврата доводится до пользователя локализованным
 * сообщением (undo-redo-R19): «нечего отменять», «нечего возвращать»,
 * «конфликт», «сеанс не найден», «сеанс закрыт», «отмена не удалась».
 *
 * Показ живёт здесь, а не в эффекте: состояние остаётся чистым, а отказ,
 * пришедший и по кнопке, и по горячей клавише, показывается одинаково.
 */
watch(
  () => undoRedoStore.pendingFailure,
  (failure): void => {
    if (failure === null) return;
    showApiError(failure);
    undoRedoStore.failureShown();
  },
);

function handleUndo(): void {
  void undoRedoStore.undo();
}

function handleRedo(): void {
  void undoRedoStore.redo();
}
</script>

<template>
  <!--
    Блок кнопок показан ровно тогда, когда продукт предлагает отмену: есть
    сеанс И выбрана фича (undo-redo-R11, versioning-R21). Горячие клавиши
    подчиняются тому же условию (R16).
  -->
  <div v-if="undoRedoStore.isUndoRedoOffered" class="undo-redo-buttons">
    <ElTooltip :content="`${t('undoRedo.undo')} (Ctrl+Z)`" placement="bottom">
      <ElButton
        :icon="RefreshLeft"
        circle
        size="small"
        :disabled="!undoRedoStore.canUndo"
        :loading="undoRedoStore.isProcessing"
        @click="handleUndo"
      />
    </ElTooltip>
    <ElTooltip :content="`${t('undoRedo.redo')} (Ctrl+Shift+Z)`" placement="bottom">
      <ElButton
        :icon="RefreshRight"
        circle
        size="small"
        :disabled="!undoRedoStore.canRedo"
        :loading="undoRedoStore.isProcessing"
        @click="handleRedo"
      />
    </ElTooltip>
  </div>
</template>

<style scoped>
.undo-redo-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
